import type { LineItemElement, LineItemSearchData } from "@/interfaces/line-item-elements-response";
import { cascadeSelectionUpdate } from "./LineItemElementsGrid.utils";
import { useCallback, useEffect, useRef, useState } from "react";
import type { FindSelectOptions } from "./input/FindSelect";
import { FindSelect } from "./input/FindSelect";
import { Button } from "./ui/button";
import type { IXactimateUserRowPayload } from "@/interfaces/payloads/xactimate";
import CreateLineItemForm from "./estimation-details/Rooms/CreateLineItemForm";
import { useRoomContext } from "@/context/RoomContext";
import type { DeleteLineItemParams } from "@/interfaces/payloads/line-item";
import { LineItemElements } from "./LineItemElements";
import { CirclePlus } from "lucide-react";
import { selectionsFromSearchResultLabel } from "./LineItemSearchUtils";
import { getSelectionsFromLineItemText } from "@rebuild/shared";
import { useDispatch, useSelector } from "@/redux/store";
import { estimationActions } from "@/redux/actions/estimation.actions";
import { estimationSelector } from "@/redux/selectors/estimation.selector";
import { useGetXactimateMappingByTemplateQuery } from "@/hooks/queries/estimates";

interface EditLineItemProps {
  itemId: string;
  toggle: () => void;
  lineItemText: string;
  onSave: (params?: DeleteLineItemParams) => Promise<void>;
  onChange?: (combined: string) => void;
  getLineItemElements: (lineItemId: string) => Promise<{ elements: LineItemElement[] }[]>;
  previousLineItem?: string;
  onCreateLineItem?: (mapping: IXactimateUserRowPayload, quantityString?: string) => boolean | Promise<boolean>;
  options: FindSelectOptions[];
  onChangeSearch?: (id: string, value: string) => void;
  /** Reads and clears the label from the last FindSelect selection (matched permutation). */
  matchedPermutation?: () => string | undefined;
  onFindOptions: (searchTerm: string) => void;
  itemSearchSelected: LineItemSearchData | undefined;
  reset: () => void;
  position: {
    top: number;
    left: number;
    width: number;
  };
  onHeightChange?: (height: number) => void;
  pageId: string;
  estimateId?: string;
  createLineItem: (secondLinePermutation: string) => Promise<void>;
}

const DUPLICATE_LINE_ERROR = "Second line cannot be identical to the first line";

export const EditLineItem = ({
  itemId,
  toggle,
  onSave,
  reset,
  lineItemText,
  options,
  onChange,
  onFindOptions,
  onChangeSearch,
  matchedPermutation,
  getLineItemElements,
  itemSearchSelected,
  position,
  onHeightChange,
  onCreateLineItem,
  pageId,
  estimateId,
  createLineItem,
  previousLineItem,
}: EditLineItemProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const findSelectRef = useRef<HTMLDivElement>(null);

  const [searchSrt, setSearchSrt] = useState<string>("");
  const [selections, setSelections] = useState<string[]>([]);
  const [elements, setElements] = useState<LineItemElement[]>([]);
  const [showCreateItem, setShowCreateItem] = useState(false);

  const [showSecondLine, setShowSecondLine] = useState(false);
  const [secondLineElements, setSecondLineElements] = useState<LineItemElement[]>([]);
  const [secondLineSelections, setSecondLineSelections] = useState<string[]>([]);
  const [disableSave, setDisableSave] = useState(true);

  const [open, setOpen] = useState(true);
  const [openSecondLine, setOpenSecondLine] = useState(false);

  const { duplicateResult, handleDuplicateCheck, clearError, duplicateError } = useRoomContext();

  const dispatch = useDispatch();
  const lineItemXactimateMapping = useSelector(estimationSelector.lineItemXactimateMapping);

  // Search results aren't in the estimate-level mapping store. Fetch every permutation's
  // mapping for the selected template so grid permutation changes also resolve.
  const { data: templateMappings } = useGetXactimateMappingByTemplateQuery(estimateId, itemSearchSelected?.id);

  const [duplicateLineError, setDuplicateLineError] = useState<string>("");

  const onSaveItem = useCallback(async () => {
    if (duplicateResult.location === "recommended" && duplicateResult.duplicateItem) {
      await onSave({
        tupleId: duplicateResult.duplicateItem.tuple.tupleId,
        pageId,
        uniqueId: duplicateResult.duplicateItem.currentItem.uniqueId,
        category: "recommended",
      });
    } else {
      await onSave();
    }
    clearError?.();

    if (showSecondLine) {
      await createLineItem(secondLineSelections.join(" ").trim());
    }
  }, [onSave, duplicateResult, clearError, pageId, showSecondLine, createLineItem, secondLineSelections]);

  const fetchElements = useCallback(async () => {
    try {
      const response = await getLineItemElements(itemId);
      const fetchedElements = response[0]?.elements ?? [];
      const selections = getSelectionsFromLineItemText(lineItemText, fetchedElements);

      setElements(fetchedElements);
      setSelections(selections);
    } catch (error) {
      console.error("Error fetching line item elements:", error);
    }
  }, [getLineItemElements, itemId, lineItemText]);

  const handleAddSecondLine = useCallback(() => {
    setShowSecondLine(true);
    setOpen(false);
    setOpenSecondLine(true);
    // Initialize second line with the same elements
    setSecondLineElements(elements);
    setSecondLineSelections(selections);
    // Disable save button until the second line is updated
    setDisableSave(true);
  }, [elements, selections]);

  const updateSecondLineSelection = useCallback(
    (index: number, newValue: string) => {
      setDisableSave(false);
      setSecondLineSelections((prev) => {
        const updated = [...prev];
        updated[index] = newValue?.trim();
        const cascaded = cascadeSelectionUpdate(secondLineElements, updated);

        // Check if the second line matches the first line
        const isDuplicate = cascaded.every((value, idx) => value === selections[idx]);
        setDuplicateLineError(isDuplicate ? DUPLICATE_LINE_ERROR : "");

        return cascaded;
      });
    },
    [selections, secondLineElements],
  );

  useEffect(() => {
    if (itemSearchSelected) {
      setElements(itemSearchSelected.elements);
      setDisableSave(false);
      const presetLabel = matchedPermutation?.();
      const nextSelections = selectionsFromSearchResultLabel(itemSearchSelected.elements, presetLabel);
      setSelections(nextSelections);
      onChange?.(nextSelections.join(" ").trim());
      setSearchSrt(nextSelections.join(" ").trim());
      return;
    } else {
      clearError?.();
    }

    fetchElements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemSearchSelected]);

  useEffect(() => {
    if (!templateMappings?.length) return;

    const current = lineItemXactimateMapping ?? [];
    const existingKeys = new Set(current.map((m) => `${m.lineItemId}|${m.lineItem}`));
    const additions = templateMappings.filter((m) => !existingKeys.has(`${m.lineItemId}|${m.lineItem}`));
    if (additions.length === 0) return;

    dispatch(
      estimationActions.setEstimationState({
        lineItemXactimateMapping: [...current, ...additions],
      }),
    );
  }, [templateMappings, lineItemXactimateMapping, dispatch]);

  useEffect(() => {
    if (selections.length > 0) {
      handleDuplicateCheck?.(selections.join(" ").trim(), previousLineItem);
    }
  }, [selections, handleDuplicateCheck, itemId, previousLineItem]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      const clickedOutsideContainer = containerRef.current && !containerRef.current.contains(target);
      const clickedOutsideFindSelectRoot = findSelectRef.current && !findSelectRef.current.contains(target);

      if (clickedOutsideContainer && clickedOutsideFindSelectRoot) {
        if (disableSave) {
          toggle();
        } else {
          onSaveItem();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [disableSave, onSave, toggle, onSaveItem]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // 👇 ResizeObserver for detect height changes
  useEffect(() => {
    if (!containerRef.current || !onHeightChange) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect) {
          onHeightChange(entry.contentRect.height);
        }
      }
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [onHeightChange]);

  const updateSelection = (index: number, newValue: string) => {
    setSelections((prev) => {
      const updated = [...prev];
      updated[index] = newValue?.trim();
      const cascaded = cascadeSelectionUpdate(elements, updated);
      onChange?.(cascaded.join(" ").trim());
      setDisableSave(false);
      return cascaded;
    });
  };

  const onFind = useCallback(
    (searchTerm: string) => {
      setSearchSrt(searchTerm);
      onFindOptions(searchTerm);
    },
    [onFindOptions],
  );

  const handleCreateItem = useCallback(() => {
    setShowCreateItem(true);
  }, []);

  const handleCreateLineItem = useCallback(
    async (mapping: IXactimateUserRowPayload) => {
      if (!onCreateLineItem) {
        return false;
      }
      const created = await onCreateLineItem(mapping);
      if (created) {
        setShowCreateItem(false);
      }
      return created;
    },
    [onCreateLineItem],
  );

  return (
    <div
      ref={containerRef}
      className="fixed z-50 w-auto max-w-[1000px] min-w-[400px] overflow-hidden rounded-md border border-stone-400 bg-white shadow-md"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <div className={`flex h-full max-h-[500px] w-auto flex-col overflow-y-auto px-8 py-4`}>
        {showCreateItem ? (
          <CreateLineItemForm lineItemText={searchSrt} onCreateLineItem={handleCreateLineItem} setShowCreateItem={setShowCreateItem} />
        ) : (
          <>
            {elements.length > 0 && (
              <>
                <LineItemElements
                  elements={elements}
                  selections={selections}
                  updateSelection={updateSelection}
                  lineNumber={1}
                  open={open}
                  hideHeader={!showSecondLine}
                  setOpen={setOpen}
                  lineItemId={itemSearchSelected?.id ?? itemId}
                  className="py-2"
                />

                {!showSecondLine && (
                  <div className="my-2 flex flex-row justify-end">
                    <Button
                      variant="outline"
                      className="my-4 flex flex-row justify-center border-2 border-dashed border-gray-200 bg-white! hover:border-gray-600!"
                      onClick={handleAddSecondLine}
                    >
                      <div className="p flex w-fit flex-row items-center gap-2">
                        <CirclePlus className="h-5 w-5" />
                        <p>Add Second Line</p>
                      </div>
                    </Button>
                  </div>
                )}

                {showSecondLine && (
                  <>
                    <LineItemElements
                      elements={secondLineElements}
                      selections={secondLineSelections}
                      updateSelection={updateSecondLineSelection}
                      lineNumber={2}
                      open={openSecondLine}
                      lineItemId={itemSearchSelected?.id ?? itemId}
                      setOpen={setOpenSecondLine}
                    />
                    {duplicateLineError && <p className="mt-2 text-sm text-red-500">{duplicateLineError}</p>}
                  </>
                )}
              </>
            )}

            <div className="space-y-2 border-t border-gray-200 bg-white pt-4">
              <FindSelect
                searchTerm={searchSrt}
                setSearchTerm={setSearchSrt}
                error={duplicateError}
                autoFocus
                options={options}
                ref={findSelectRef}
                onFindOptions={onFind}
                onChange={onChangeSearch}
                placeholder="Search for line item"
                onCreateItem={handleCreateItem}
              />

              <div className="flex w-full flex-row justify-between gap-2">
                <Button className="!max-h-[28px] !px-2 text-sm text-[#000]" variant="outline" onClick={toggle}>
                  Cancel
                </Button>
                <Button onClick={onSaveItem} disabled={disableSave} className="!max-h-[28px] !bg-[#000] !px-2 text-sm text-white">
                  Save
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
